import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Filter, ShoppingBag, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import debounce from 'lodash.debounce';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/shopify/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        // Fallback to static categories
        try {
          const fallback = await axios.get(`${API}/products/categories`);
          setCategories(fallback.data.categories);
        } catch (e) {
          console.error('Fallback categories also failed:', e);
        }
      }
    };
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Debounced search function
  const searchProducts = useCallback(
    debounce(async (searchQuery, category, minPrice, maxPrice) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.append('q', searchQuery);
        if (category) params.append('category', category);
        if (minPrice) params.append('min_price', minPrice);
        if (maxPrice) params.append('max_price', maxPrice);
        
        // Try Shopify API first, fallback to static products
        let response;
        try {
          response = await axios.get(`${API}/shopify/products/search?${params.toString()}`);
        } catch (shopifyError) {
          console.log('Shopify API unavailable, using fallback');
          response = await axios.get(`${API}/products/search?${params.toString()}`);
        }
        setResults(response.data.products);
        
        // Track search event
        if (searchQuery) {
          axios.post(`${API}/analytics/track?event_type=product_search&page=${searchQuery}`).catch(() => {});
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Trigger search when inputs change
  useEffect(() => {
    if (isOpen) {
      searchProducts(query, selectedCategory, priceRange.min, priceRange.max);
    }
  }, [query, selectedCategory, priceRange, isOpen, searchProducts]);

  // Track product clicks
  const handleProductClick = async (product) => {
    try {
      await axios.post(`${API}/analytics/track?event_type=product_click&product_id=${product.id}&product_name=${encodeURIComponent(product.name)}`);
    } catch (error) {
      // Silent fail for analytics
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-start justify-center pt-20 px-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Search Products</h2>
            <button 
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for earrings, pendants, bracelets..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-12 py-6 text-lg border-2 border-amber-300 focus:border-amber-500"
              autoFocus
            />
            {query && (
              <button 
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-500">Filters:</span>
            </div>
            
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-amber-500 focus:outline-none"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
            
            {/* Price Range */}
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Min $"
                value={priceRange.min}
                onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                className="w-20 px-2 py-1.5 text-sm"
              />
              <span className="text-gray-400">-</span>
              <Input
                type="number"
                placeholder="Max $"
                value={priceRange.max}
                onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                className="w-20 px-2 py-1.5 text-sm"
              />
            </div>
            
            {/* Clear Filters */}
            {(selectedCategory || priceRange.min || priceRange.max) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedCategory('');
                  setPriceRange({ min: '', max: '' });
                }}
                className="text-amber-600 hover:text-amber-700"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
        
        {/* Results */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              <span className="ml-3 text-gray-500">Searching...</span>
            </div>
          ) : results.length > 0 ? (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Found {results.length} product{results.length !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {results.map((product) => (
                  <a
                    key={product.id}
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleProductClick(product)}
                    className="block"
                  >
                    <Card className="group hover:shadow-lg transition-all hover:border-amber-300 overflow-hidden">
                      <CardContent className="p-0">
                        <div className="aspect-square overflow-hidden bg-gradient-to-br from-amber-50 to-teal-50">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-contain p-2 group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-3">
                          <h4 className="font-medium text-gray-900 text-sm line-clamp-2 group-hover:text-amber-600 transition-colors">
                            {product.name}
                          </h4>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-lg font-bold text-amber-600">
                              ${product.price.toFixed(2)}
                            </span>
                            {product.category && (
                              <Badge variant="secondary" className="text-xs">
                                {product.category}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            </>
          ) : query || selectedCategory || priceRange.min || priceRange.max ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No products found</p>
              <p className="text-sm text-gray-400 mt-2">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Start typing to search products</p>
              <p className="text-sm text-gray-400 mt-2">Search by name, type, or browse by category</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <a
            href="https://gifts.charmingcreationsgifts.com/collections/all"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-amber-600 hover:text-amber-700 font-medium"
          >
            <ShoppingBag className="w-4 h-4" />
            View Full Collection on Shopify
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductSearch;
